export type JsonCallbackValue = string | number | boolean | null | { [key: string]: JsonCallbackValue };

export interface JsonCallbackCall {
  name: string;
  args: JsonCallbackValue[];
}

const CALL_PATTERN = /^\s*([A-Za-z_$][\w$]*)\s*\(([\s\S]*)\)\s*;?\s*$/;

const KEYWORDS: Record<string, JsonCallbackValue> = { null: null, undefined: null, true: true, false: false };

/**
 * Reads a `jsonCallback` (`RedirectDynamicPage('https://…', 'planId=1', 3)`) as a function name and its
 * literal arguments, without evaluating it. Only strings, numbers, booleans, `null` / `undefined` and plain
 * objects of those are accepted; anything else returns `null`.
 */
export function parseJsonCallback(source: string | null | undefined): JsonCallbackCall | null {
  const match = CALL_PATTERN.exec(source ?? '');
  if (!match) return null;
  const reader = new LiteralReader(match[2]);
  try {
    const args = reader.readList(')');
    return reader.atEnd() ? { name: match[1], args } : null;
  } catch {
    return null;
  }
}

class LiteralReader {
  private _index = 0;

  constructor(private readonly _text: string) {}

  atEnd(): boolean {
    this.skipSpace();
    return this._index >= this._text.length;
  }

  readList(closing: string): JsonCallbackValue[] {
    const values: JsonCallbackValue[] = [];
    this.skipSpace();
    if (this._index >= this._text.length || this.peek() === closing) return values;
    for (;;) {
      values.push(this.readValue());
      this.skipSpace();
      if (this.peek() !== ',') return values;
      this._index++;
    }
  }

  private readValue(): JsonCallbackValue {
    this.skipSpace();
    const char = this.peek();
    if (char === "'" || char === '"') return this.readString(char);
    if (char === '{') return this.readObject();
    if (char === '-' || /\d/.test(char)) return this.readNumber();
    const word = this.readWord();
    if (word in KEYWORDS) return KEYWORDS[word];
    throw new Error(`Unexpected token "${word}"`);
  }

  private readString(quote: string): string {
    let value = '';
    this._index++;
    while (this._index < this._text.length) {
      const char = this._text[this._index++];
      if (char === quote) return value;
      value += char === '\\' ? this._text[this._index++] : char;
    }
    throw new Error('Unterminated string');
  }

  private readNumber(): number {
    const match = /^-?\d+(\.\d+)?/.exec(this._text.slice(this._index));
    if (!match) throw new Error('Invalid number');
    this._index += match[0].length;
    return Number(match[0]);
  }

  private readWord(): string {
    const match = /^[A-Za-z_$][\w$]*/.exec(this._text.slice(this._index));
    if (!match) throw new Error('Unexpected character');
    this._index += match[0].length;
    return match[0];
  }

  private readObject(): { [key: string]: JsonCallbackValue } {
    const value: { [key: string]: JsonCallbackValue } = {};
    this._index++;
    for (;;) {
      this.skipSpace();
      if (this.peek() === '}') {
        this._index++;
        return value;
      }
      const quote = this.peek();
      const key = quote === "'" || quote === '"' ? this.readString(quote) : this.readWord();
      if (key === '__proto__') throw new Error('Forbidden key');
      this.skipSpace();
      if (this.peek() !== ':') throw new Error('Expected ":"');
      this._index++;
      value[key] = this.readValue();
      this.skipSpace();
      if (this.peek() === ',') this._index++;
      else if (this.peek() !== '}') throw new Error('Expected "," or "}"');
    }
  }

  private peek(): string {
    return this._text[this._index] ?? '';
  }

  private skipSpace(): void {
    while (/\s/.test(this.peek())) this._index++;
  }
}
